/**
 * Copyright 2026 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import { PropertyValues } from 'lit';
import { property } from 'lit/decorators.js';

import { SpectrumElement } from '@adobe/spectrum-wc-core/element/index.js';
import { SizedMixin } from '@adobe/spectrum-wc-core/mixins/index.js';

import {
  STATUS_LIGHT_VALID_SIZES,
  type StatusLightVariant,
} from './StatusLight.types.js';

/**
 * A status light is a great way to convey semantic meaning and the condition of an entity, such as statuses and categories. It provides visual indicators through colored dots accompanied by descriptive text.
 *
 * @slot - The text label of the status light.
 * @attribute {ElementSize} size - The size of the status light.
 */
export abstract class StatusLightBase extends SizedMixin(SpectrumElement, {
  validSizes: STATUS_LIGHT_VALID_SIZES,
  noDefaultSize: true,
}) {
  // ─────────────────────────
  //     API TO OVERRIDE
  // ─────────────────────────

  /**
   * @internal
   *
   * A readonly array of the valid color variants for the status light.
   *
   * This is an actual internal property, intended not for customer use
   * but for use in internal validation logic, stories, tests, etc.
   *
   * Because S1 and S2 support different color variants, the value of this
   * property must be set in each subclass.
   */
  static readonly VARIANTS_COLOR: readonly string[];

  /**
   * @internal
   *
   * A readonly array of the valid semantic variants for the status light.
   *
   * This is an actual internal property, intended not for customer use
   * but for use in internal validation logic, stories, tests, etc.
   *
   * Because S1 and S2 support different semantic variants, the value of this
   * property must be set in each subclass.
   */
  static readonly VARIANTS_SEMANTIC: readonly string[];

  /**
   * @internal
   *
   * A readonly array of all valid variants for the status light.
   *
   * This is an actual internal property, intended not for customer use
   * but for use in internal validation logic, stories, tests, etc.
   *
   * Because S1 and S2 support different variants, the value of this
   * property must be set in each subclass.
   */
  static readonly VARIANTS: readonly string[];

  /**
   * @internal
   *
   * The variant of the status light.
   *
   * This is a public property, but its valid values vary between S1 and S2,
   * so the property (and its docs) need to be redefined in each subclass.
   *
   * The type declared here is a union of the valid values for S1 and S2,
   * and should be narrowed in each subclass.
   */
  @property({ type: String, reflect: true })
  public variant: StatusLightVariant = 'neutral';

  // ──────────────────────
  //     IMPLEMENTATION
  // ──────────────────────

  protected override updated(changes: PropertyValues): void {
    super.updated(changes);
    if (window.__swc?.DEBUG) {
      const constructor = this.constructor as typeof StatusLightBase;
      // @ts-expect-error -- intentional runtime guard: 1st-gen consumers may pass 'accent'
      if (this.variant === 'accent') {
        window.__swc.warn(
          this,
          `<${this.localName}> does not support the "accent" variant in Spectrum 2. Use "neutral" or "info" depending on intent.`,
          'https://spectrum-web-components.adobe.com/?path=/docs/status-light-migration-guide--docs',
          { level: 'deprecation' }
        );
      } else if (!constructor.VARIANTS.includes(this.variant)) {
        window.__swc.warn(
          this,
          `<${this.localName}> element expects the "variant" attribute to be one of the following:`,
          'https://opensource.adobe.com/spectrum-web-components/components/status-light/#variants',
          {
            issues: [...constructor.VARIANTS],
          }
        );
      }
      if (this.hasAttribute('disabled')) {
        window.__swc.warn(
          this,
          `<${this.localName}> does not support the "disabled" attribute. It was deprecated in Spectrum 1 and has been removed in Spectrum 2.`,
          'https://spectrum-web-components.adobe.com/?path=/docs/status-light-migration-guide--docs',
          { level: 'deprecation' }
        );
      }
    }
  }
}
